/**
 * Presents a UI that is suited for modifying a given user's data.
 * ## Fields
 * Name | Description
 * --- | ---
 * model | The `User` model associated with the view.
 * @module app/views/admin/users/Edit
 */
import Backbone from "backbone";
import _ from "underscore";
import $ from "jquery";
import GroupCollection from "app/models/GroupCollection";
import Field from "core/models/Field";
import CollectionTableView from "core/views/CollectionTableView";
import Group from "app/models/Group";
import viewTemplate from "views/admin/users/Edit";
import createViewTemplate from "views/admin/users/Create";
import auth from "app/auth";
import ErrorUtils from "app/utils/ErrorUtils";
import Helper from "app/ejs";
import session from "app/Session";
import urlService from "app/UrlService";
import messenger from "app/Messenger";

function objectifyForm(form) {
  //serialize data function

  var returnObject = {};
  for (var i = 0; i < form.length; i++) {
    returnObject[form[i]["name"]] = form[i]["value"];
  }
  return returnObject;
}

export default Backbone.View.extend({
  template: Helper.template(viewTemplate),

  className: "admin-user-edit",

  /**
   * Initializes the view.
   * @param  {object} options The options hash.
   * @param {User} options.model The user model associated with the view.
   * @param {Array} options.organizations An array of `Organization` models.
   */
  initialize: function (options) {
    this.options = options;

    this.model = this.options.model;
    this._orgs = this.options.organizations;
  },

  events: {
    "click .change-password-button": "updatePassword",
  },

  _organizationList: function () {
    var result = _.map(this._orgs, function (item) {
      return {
        value: item.id,
        text: item.get("PrimaryName"),
      };
    });

    result.unshift({
      value: "",
      text: "No Organization",
    });

    return result;
  },

  /**
   * Gets the user password form data as JSON
   * @return {Object} A hash of the password form data
   */
  passwordFormData: function () {
    return objectifyForm(this.$("#password-form").serializeArray());
  },

  /**
   * Gets the user form data as JSON
   * @return {Object} A hash of the form data
   */
  formData: function () {
    return objectifyForm(this.$("#form").serializeArray());
  },

  /**
   * Updates the password on the model using password form inputs. Related user messages are generated within this function.
   * @return {void|PromisesAPlus.Thenable<void>|*} A promise which resolves if the update is successful and rejects if an error occurs.
   */
  updatePassword: function () {
    var $passwordForm = this.$("#password-form");
    var data = this.passwordFormData();

    return this.model
      .updatePassword(
        data.oldPassword,
        data.newPassword,
        session.hasRole("admin")
      )
      .done(function () {
        $passwordForm.clearForm();
        messenger.success("Password updated successfully");
      })
      .fail(
        ErrorUtils.errorHandler({
          activity: "updating the password",
        })
      );
  },

  /**
   * Submits the form, propagating the data to the `user` model.
   */
  save: function () {
    var deferred = new $.Deferred();
    var self = this;

    this.model.save(this.formData(), {
      success: function () {
        deferred.resolve(self.model);
      },
      error: ErrorUtils.apiHandler({
        activity: "saving the user",
      }),
    });

    return deferred.promise();
  },

  render: function () {
    this.$el.html(
      this.template(
        _.extend({}, this.options, {
          organizations: this._organizationList(),
          isAdmin: session.hasRole("admin"),
          passwordModel: new Backbone.Model({
            oldPassword: "",
            newPassword: "",
            confirmPassword: "",
          }),
          createView: Helper.template(createViewTemplate),
          token: auth.token,
          apiBuilder: urlService.apiBuilder,
        })
      )
    );

    if (session.hasRole("admin")) {
      var groupCollection = new GroupCollection([]);
      var self = this;

      groupCollection.fetch({
        success: function () {
          var fields = [
            new Field(Group.fields["id"]).decorate(
              {
                editor: "custom",
                ViewerView: {
                  tagName: "input",
                  attributes: {
                    type: "checkbox",
                  },

                  template: function () {
                    return "";
                  },

                  events: {
                    click: function () {
                      this.setValue(this.getControlValue());
                      this.onChange();
                    },
                  },

                  postRender: function () {
                    var self = this;

                    if (this.load) {
                      this.load.done(function (options) {
                        if (options.disabled) {
                          self.$el.attr("disabled", "disabled");
                        } else {
                          self.$el.removeAttr("disabled");
                        }
                      });
                    }
                  },

                  getControlValue: function () {
                    return this.el.checked;
                  },

                  setControlValue: function (value) {
                    if (this.el) {
                      this.el.checked = value;
                    }
                  },

                  startLoad: function () {
                    var deferred = new $.Deferred();
                    var model = this.options.model;

                    model.getUsers(function (err, users) {
                      if (err) {
                        return deferred.reject(err);
                      }

                      deferred.resolve({
                        value: _.contains(
                          _.pluck(users.models, "id"),
                          self.model.id
                        ),
                        disabled: !!model.get("organization"),
                      });
                    });

                    return deferred.promise();
                  },
                },
              },
              {
                getter: function (model) {
                  return _.contains(
                    _.pluck(model.get("users"), "id"),
                    self.model.id
                  );
                },
                setter: function (model, value) {
                  if (value) {
                    self.model.addToGroup(model);
                    model.get("users").push(self.model.toJSON());
                  } else {
                    self.model.removeFromGroup(model);
                    model.set(
                      "users",
                      _.filter(model.get("user"), function (userJson) {
                        return userJson.id != self.model.id;
                      })
                    );
                  }
                },
              }
            ),
            new Field(Group.fields["name"]),
          ];
          var groupView = new CollectionTableView({
            fields: fields,
            collection: groupCollection,
            allowSave: false,
            allowDelete: false,
            mode: "view",
          });

          groupView.render();
          groupView.$el.appendTo(
            self.$("#permissions .slider-outer .slider-content")
          );
        },
        error: ErrorUtils.apiHandler(),
      });
    }
  },
});



//////////////////
// WEBPACK FOOTER
// ./public/js/app/views/admin/users/Edit.js
// module id = 486
// module chunks = 1